import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IncListCompareDialog } from './IncListCompareDialog';

export const IncDataSrcCompare = () => {
  const { navState } = useNavState();
  const { hasRole } = usePrincipal();
  const { openDialog } = useDialog();
  
  const rsrc = useRsrc();

  const [pols, setPols] = useState([]);

  const polIds = useMemo(() => {
    if (Array.isArray(navState?.polIds) && navState.polIds.length > 0) {
      return navState.polIds;
    }
    if (Array.isArray(navState?.pols) && navState.pols.length > 0) {
      return navState.pols.map(pol => pol.id).filter(Boolean);
    }
    return [];
  }, [navState?.polIds, navState?.pols]);

  const polKey = useMemo(() => polIds.join('|'), [polIds]);

  useEffect(() => {
    if (polIds.length !== 2) {
      setPols([]);
      return;
    }

    const rowsReq = { expr: ExprUtils.whereParams('id', polIds) };
    rsrc.rows('gp', 'POL', rowsReq).then((polRows) => {
      const sortedPols = new Array(polIds.length);
      polRows.forEach((pol) => {
        const index = polIds.indexOf(pol.id);
        if (index >= 0) {
          sortedPols[index] = pol;
        }
      });
      setPols(sortedPols.filter(Boolean));
    });
  }, [polKey]);

  const augFieldDefs = useCallback((fieldToFieldDef) => {
    if (pols.length !== 2) {
      return;
    }

    ObjUtils.clearObj(fieldToFieldDef);

    const [leftPol, rightPol] = pols;
    fieldToFieldDef.name = { field: 'name'};
    fieldToFieldDef.code = { field: 'code', autoGrouping: true  };
    fieldToFieldDef.entity = { field: 'entity' };

    fieldToFieldDef.match = {
        field: 'match',
        width: 70,
        cellClass: 'ag-center-cell',
        type: 'MATCH'
    };

    fieldToFieldDef.pol0 =  { 
      field: 'pol0', 
      displayName: (leftPol?.name ?? 'Policy A') + ' Values',
      children: [
          { displayName: 'Transaction Column', field: 'trxCol0' },
          { displayName: 'Operator', field: 'operator0', minWidth: 205 },
          { displayName: 'Value', field: 'value0', maxWidth: 250 }
      ]
    };

    fieldToFieldDef.pol1 =  { 
      field: 'pol1', 
      displayName: (rightPol?.name ?? 'Policy B') + ' Values',
      children: [
          { displayName: 'Transaction Column', field: 'trxCol1' },
          { displayName: 'Operator', field: 'operator1', minWidth: 205 },
          { displayName: 'Value', field: 'value1', maxWidth: 250 }
      ]
    };
  }, [pols]);
  
  const clientSideRowData = useCallback((setDataSource) => {
    const reqParam = {
      polId: polIds[0],
      polIds: polIds
    };
    rsrc.data('gp', 'INC_DATA_SRC_DATA_PLUGIN', reqParam).then(res => {
      setDataSource(res);
    })
  }, []);



  const openIncListCompareDialog = (rowData) => {
    const data = {
        title0: 'List from: ' + pols[0].name,
        title1: 'List from: ' + pols[1].name,
        value0: rowData.value0,
        value1: rowData.value1
    }
    openDialog(IncListCompareDialog, { data })
  }
  
  const getContextMenuItems = (params) => {
    let contextMenuItems = [];

    const data = params.node.data;
    let listOperators = ['Is One Of', 'Is Not One Of'];
    const incListApplicable = (listOperators.includes(data.operator0) || listOperators.includes(data.operator1));
    const hasIncListView = hasRole('GP_INC_LIST_VIEW');
    if (incListApplicable && hasIncListView) {
      contextMenuItems.push({
        name: 'Compare Inclusion Lists',
        icon: fas.faSignInAlt,
        action: () => openIncListCompareDialog(data)
      });
    }
    return contextMenuItems;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', width: '100%'}}>
      <Box sx={{ key: pols.length, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Title icon={fas.faBalanceScale} title="Inclusion Data Source Compare" />
      </Box>
      {pols.length === 2 && (
      <EntityList
        module="gp"
        entity="INC_DATA_SRC"
        showTitle={false}
        augFieldDefs={augFieldDefs}
        clientSideRowData={clientSideRowData}
        gridOptions={{
          treeData: true,
          defaultExpandLevel: 0,
          getContextMenuItems
        }}
      />
      )}
    </Box>
  );
};
